import React, {Fragment, useMemo} from 'react';
import {DataTablePage} from '@common/datatable/page/data-table-page';
import {Trans} from '@ui/i18n/trans';
import {DeleteSelectedItemsAction} from '@common/datatable/page/delete-selected-items-action';
import {DataTableEmptyStateMessage} from '@common/datatable/page/data-table-emty-state-message';
import movieNightImage from './movie-night.svg';
import {useTitleIndexFilters} from '@app/titles/use-title-index-filters';
import {TitlesDatatableFilters} from '@app/admin/titles/titles-datatable-filters';

import {DialogTrigger} from '@ui/overlays/dialog/dialog-trigger';
import {RequestPrimaryFactsForm} from '@app/admin/requetes/lien-editor/request-primary-facts-form';
import {Button} from '@ui/buttons/button';
import {ColumnConfig} from '@common/datatable/column-config';
import {RequestTitle} from '@app/titles/models/Request';
import {Link} from 'react-router-dom';
import {TitlePoster} from '@app/titles/title-poster/title-poster';
import {TitleLink} from '@app/titles/title-link';
import {ChipList} from '@ui/forms/input-field/chip-field/chip-list';
import {Chip} from '@ui/forms/input-field/chip-field/chip';
import {FormattedDate} from '@ui/i18n/formatted-date';
import {Tooltip} from '@ui/tooltip/tooltip';
import {IconButton} from '@ui/buttons/icon-button';
import {EditIcon} from '@ui/icons/material/Edit';
import {useAuth} from '@common/auth/use-auth';
import {IconSquarePlus2} from '@tabler/icons-react';
import {AddTaskIcon} from '@ui/icons/material/AddTask';
import {RequestCompleteForm} from '@app/admin/requetes/lien-editor/request-complete-form';
import {useLinkFiltersData} from '@app/liens/use-link-filters';

function DatatablePage() {
  const {hasPermission} = useAuth();
  const RequestDatatableColumns: ColumnConfig<RequestTitle>[] = [
    {
      key: 'author',
      allowsSorting: true,
      header: () => <Trans message="Author" />,
      body: title => (
        <Link to={`get-user/${title.author}`}>{title.author}</Link>
      ),
      width: 'w-124 flex-shrink-0',
    },
    {
      key: 'title',
      allowsSorting: true,
      width: 'flex-3',
      visibleInMode: 'all',
      header: () => <Trans message="Title" />,
      body: title =>
        title.post ? (
          <div className="flex items-center gap-12">
            <TitlePoster
              title={title.post}
              srcSize="sm"
              size="w-32"
              showLastLink={false}
              aspect="aspect-square"
            />
            <div className="min-w-0 overflow-hidden">
              <div className="overflow-hidden overflow-ellipsis">
                <TitleLink title={title.post} target="_blank" />
              </div>
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-12">
            <div className="min-w-0 overflow-hidden">
              <div className="overflow-hidden overflow-ellipsis">
                <Link to={title.url} target="_blank">
                  {title.title}
                </Link>
              </div>
            </div>
          </div>
        ),
    },
    {
      key: 'category',
      allowsSorting: true,
      header: () => <Trans message="Category" />,
      body: title => title.cat.name,
    },
    {
      key: 'saison',
      allowsSorting: true,
      header: () => <Trans message="Saison" />,
      body: title =>
        title.saison !== null && title.serie ? title.saison : null,
    },
    {
      key: 'episodes',
      allowsSorting: true,
      header: () => <Trans message="Episodes" />,
      body: title =>
        title.episodes && title.serie ? (
          <ChipList color={'chip'} radius="rounded" size="xs">
            {title.episodes.map(episode => (
              <Chip key={episode} selectable>
                {episode}
              </Chip>
            ))}
          </ChipList>
        ) : null,
      width: 'w-124 flex-shrink-0',
    },
    {
      key: 'resolver',
      allowsSorting: true,
      header: () => <Trans message="Resolver" />,
      body: title => (
        <Link to={`get-user/${title.resolver}`}>{title.resolver}</Link>
      ),
      width: 'w-124 flex-shrink-0',
    },
    {
      key: 'status',
      allowsSorting: true,
      header: () => <Trans message="Status" />,
      body: title => (
        <ChipList
          color={
            title.status === 0
              ? 'chip'
              : title.status === 1
                ? 'primary'
                : 'positive'
          }
          radius="rounded"
          size="xs"
        >
          <Chip key={title.id} selectable>
            <Trans
              message={
                title.status === 0
                  ? 'Pending'
                  : title.status === 1
                    ? 'Processing'
                    : 'Resolved'
              }
            />
          </Chip>
        </ChipList>
      ),
      width: 'w-124 flex-shrink-0',
    },
    {
      key: 'created_at',
      allowsSorting: true,
      width: 'w-124 flex-shrink-0',
      header: () => <Trans message="Created at" />,
      body: title => <FormattedDate preset={'short'} date={title.created_at} />,
    },
    {
      key: 'actions',
      header: () => <Trans message="Actions" />,
      hideHeader: true,
      visibleInMode: 'all',
      align: 'end',
      width: 'w-84 flex-shrink-0',
      body: title => (
        <Fragment>
          {hasPermission('requests.update') && (
            <DialogTrigger type="modal">
              <Tooltip label={<Trans message="Edit" />}>
                <IconButton
                  size="md"
                  className="text-gray-500"
                  onPointerDown={e => e.preventDefault()}
                >
                  <EditIcon />
                </IconButton>
              </Tooltip>
              <RequestPrimaryFactsForm requestId={title.id} />
            </DialogTrigger>
          )}
          {title.status !== 2 && (
            <DialogTrigger type="modal">
              <Tooltip label={<Trans message="Complete request" />}>
                <IconButton
                  size="md"
                  className="text-gray-500"
                  onPointerDown={e => e.preventDefault()}
                >
                  <AddTaskIcon />
                </IconButton>
              </Tooltip>
              <RequestCompleteForm requestId={title.id} />
            </DialogTrigger>
          )}
        </Fragment>
      ),
    },
  ];
  return RequestDatatableColumns;
}

export function RequetesDatatablePage() {
  const {filters, filtersLoading} = useTitleIndexFilters();
  const {hasPermission} = useAuth();
  const filtersDefault = useLinkFiltersData();
  const mergedFilters = useMemo(() => {
    return [...filters, ...TitlesDatatableFilters(filtersDefault)];
  }, [filters]);

  return (
    <DataTablePage
      endpoint="requests"
      enableSelection={hasPermission('requests.delete')}
      title={<Trans message="Requests" />}
      columns={DatatablePage()}
      filters={mergedFilters}
      filtersLoading={filtersLoading}
      actions={<Actions />}
      selectedActions={<DeleteSelectedItemsAction />}
      emptyStateMessage={
        <DataTableEmptyStateMessage
          image={movieNightImage}
          title={<Trans message="No titles have been created yet" />}
          filteringTitle={<Trans message="No matching titles" />}
        />
      }
    />
  );
}

function Actions() {
  return (
    <Fragment>
      <DialogTrigger type="modal">
        <Button
          variant="raised"
          color={'primary'}
          startIcon={<IconSquarePlus2 />}
        >
          <Trans message="Add new request" />
        </Button>
        <RequestPrimaryFactsForm />
      </DialogTrigger>
    </Fragment>
  );
}
